import { PlusIcon, SearchIcon } from "lucide-react";
import styles from "./JobTaskTab.module.scss";
import classNames from "classnames/bind";
import { useEffect, useMemo, useRef, useState } from "react";
import PrimarySelectInput from "~/components/common/primary-select-input/PrimarySelectInput";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import { ColumnType } from "~/utils/types/common";
import { useAppDispatch, useAppSelector } from "~/redux/hooks";
import {
  handleCreateJobTask,
  handleGetOverviewJobTaskList,
  handleUpdateJobTask,
} from "~/thunks/job-details/jobDetailsThunk";
import { notify } from "~/utils/helper";
import dayjs from "dayjs";
import { ActionIcon } from "~/assets/icons";
import { BaseTable } from "~/components/common";
import AddNewTaskModal from "./add-new-task/AddNewTaskModal";
import { IPayloadCreateJobTask } from "~/utils/interface/jobDetails";
import ModalAlertDelete from "~/components/common/modal-alert-delete/ModalAlertDelete";
import jobDetailsApi from "~/apis/jobDetails";
import LoadingMainContent from "~/components/common/loading-main-content/LoadingMainContent";

const cx = classNames.bind(styles);

interface JobTaskTabProps {
  jobId: string;
}

// Same statuses the Edit Task modal offers.
const TASK_STATUS_OPTIONS = ["In Progress", "Completed"];

const JobTaskTab = ({ jobId }: JobTaskTabProps) => {
  const dispatch = useAppDispatch();
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const { overviewJobTaskList } = useAppSelector((state) => state.jobDetails);
  const [isOpendModalDelete, setIsOpendModalDelete] = useState(false);
  const [jobTaskIdToDelete, setJobTaskIdToDelete] = useState<string | null>(
    null
  );
  const [isOpenModalAddTask, setIsOpenModalAddTask] = useState(false);
  const [selectedJobTask, setSelectedJobTask] = useState<any>(null);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [isLoadingModalAddTask, setIsLoadingModalAddTask] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null);

  // Inline status change from the list (#41). type + subContractorId are sent back unchanged because
  // the BE update schema requires subContractorId and defaults a missing type to "Adjuster Meeting".
  const handleInlineStatusChange = async (jobTask: any, status: string) => {
    if (status === jobTask.status) return;
    setUpdatingStatusId(String(jobTask.id));
    try {
      await dispatch(
        handleUpdateJobTask({
          id: String(jobTask.id),
          data: { status, type: jobTask.type, subContractorId: Number(jobTask.subContractorId) } as unknown as IPayloadCreateJobTask,
        })
      ).unwrap();
      await dispatch(handleGetOverviewJobTaskList({ id: jobId, params: {} })).unwrap();
      notify("success", "Task status updated");
    } catch (error: any) {
      notify("error", `Failed to update task status: ${error?.response?.data?.message || "Unknown error"}`);
    } finally {
      setUpdatingStatusId(null);
    }
  };

  // Search and Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    if (jobId) {
      setIsLoading(true);
      dispatch(
        handleGetOverviewJobTaskList({
          id: jobId,
          params: {},
        })
      )
        .unwrap()
        .then((res) => {
          setIsLoading(false);
        })
        .catch((error: any) => {
          notify(
            "error",
            `Failed to fetch job task list: ${error.response.data.message}`
          );
          setIsLoading(false);
        });
    }
  }, [dispatch, jobId]);

  // Status filter options
  const statusOptions = [
    { value: "", label: "All Tasks" },
    { value: "In Progress", label: "Incomplete" },
    { value: "Completed", label: "Completed" },
  ];

  // Filter and search logic
  const filteredJobTasks = useMemo(() => {
    let filtered = overviewJobTaskList?.responses || [];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((jobTask: any) => {
        const title = jobTask.title?.toLowerCase() || "";
        const description = jobTask.description?.toLowerCase() || "";
        return title.includes(query) || description.includes(query);
      });
    }

    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter(
        (jobTask: any) => jobTask.status === statusFilter
      );
    }

    return filtered;
  }, [overviewJobTaskList?.responses, searchQuery, statusFilter]);

  // Action Menu Component
  const ActionMenu = ({ jobTask, onClose, position }: any) => {
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          menuRef.current &&
          !menuRef.current.contains(event.target as Node)
        ) {
          onClose();
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);

    const handleEdit = () => {
      setSelectedJobTask(jobTask);
      setModalMode("edit");
      setIsOpenModalAddTask(true);
      onClose();
    };

    const handleDelete = () => {
      setJobTaskIdToDelete(jobTask.id.toString());
      setIsOpendModalDelete(true);
      onClose();
    };

    return (
      <div
        ref={menuRef}
        className={cx("action-menu")}
        style={{
          top: position.top,
          left: position.left,
        }}
      >
        <button className={cx("menu-item")} onClick={handleEdit}>
          Edit
        </button>
        <button className={cx("menu-item")} onClick={handleDelete}>
          Delete
        </button>
      </div>
    );
  };

  const handleActionClick = (event: React.MouseEvent, jobTask: any) => {
    event.stopPropagation();

    if (activeMenu === jobTask.id) {
      setActiveMenu(null);
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom + 5,
      left: rect.left - 100,
    });
    setActiveMenu(jobTask.id);
  };

  const columns: Array<ColumnType> = [
    {
      id: "task",
      labelHeader: "Task",
      width: "38%",
      renderCell: (jobTask: any, index: number) => (
        <div className={cx("task-container")}>
          <p className={cx("task-title")}>{jobTask.title || ""}</p>
          <p className={cx("task-description")}>{jobTask.description || ""}</p>
        </div>
      ),
    },
    {
      id: "status",
      labelHeader: "Status",
      width: "15%",
      renderCell: (jobTask: any, index: number) => (
        <div
          className={cx("status-cell", {
            "in-progress": jobTask.status === "In Progress",
            completed: jobTask.status === "Completed",
          })}
        >
          <select
            className={cx("status", "status-select")}
            value={jobTask.status || ""}
            disabled={updatingStatusId === String(jobTask.id)}
            onChange={(e) => handleInlineStatusChange(jobTask, e.target.value)}
            onClick={(e) => e.stopPropagation()}
          >
            {!TASK_STATUS_OPTIONS.includes(jobTask.status) && (
              <option value={jobTask.status || ""}>{jobTask.status || "-"}</option>
            )}
            {TASK_STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>
      ),
    },
    {
      id: "assignedTo",
      labelHeader: "Assigned To",
      width: "20%",
      renderCell: (jobTask: any, index: number) => (
        <div className={cx("assigned-to-container")}>
          <div className={cx("avatar")} />
          <p className={cx("assigned-to")}>{jobTask.account?.username || ""}</p>
        </div>
      ),
    },
    {
      id: "dateAdded",
      labelHeader: "Date Added",
      width: "20%",
      renderCell: (jobTask: any, index: number) => (
        <p className={cx("date-added")}>
          {jobTask.createdAt
            ? dayjs(jobTask.createdAt).format("YYYY-MM-DD")
            : ""}
        </p>
      ),
    },
    {
      id: "actions",
      labelHeader: "",
      width: "7%",
      renderCell: (jobTask: any, index: number) => (
        <div className={cx("action-icon-container")}>
          <div onClick={(e) => handleActionClick(e, jobTask)}>
            <ActionIcon className={cx("action-icon")} />
          </div>
          {activeMenu === jobTask.id && (
            <ActionMenu
              jobTask={jobTask}
              onClose={() => setActiveMenu(null)}
              position={menuPosition}
            />
          )}
        </div>
      ),
    },
  ];

  const onDeleteWorkOrderConfirm = async () => {
    if (jobTaskIdToDelete) {
      try {
        await jobDetailsApi.deleteJobTask(jobTaskIdToDelete);
        notify("success", "Job task deleted successfully");
        setIsOpendModalDelete(false);
        setJobTaskIdToDelete(null);
        setIsLoading(true);
        dispatch(handleGetOverviewJobTaskList({ id: jobId, params: {} }))
          .unwrap()
          .then((res) => {
            setIsLoading(false);
          })
          .catch((error: any) => {
            notify("error", `Failed to fetch job task list: ${error}`);
            setIsLoading(false);
          });
      } catch (error: any) {
        notify(
          "error",
          `Failed to delete work order: ${error?.response?.data?.message || "Unknown error"
          }`
        );
      }
    } else {
      notify("error", "Work order not found");
    }
  };

  const handleOpenModalAddTask = () => {
    setSelectedJobTask(null);
    setModalMode("add");
    setIsOpenModalAddTask(true);
  };

  const handleCloseModalAddTask = () => {
    setIsOpenModalAddTask(false);
    setSelectedJobTask(null);
    setModalMode("add");
  };

  const handleSaveModalAddTask = async (
    data: IPayloadCreateJobTask,
    mode: "add" | "edit"
  ) => {
    if (mode === "add") {
      setIsLoadingModalAddTask(true);
      dispatch(handleCreateJobTask(data))
        .unwrap()
        .then((res) => {
          notify("success", "Job task created successfully");
          setIsOpenModalAddTask(false);
          setSelectedJobTask(null);
          setIsLoadingModalAddTask(false);
          setIsLoading(true);
          dispatch(
            handleGetOverviewJobTaskList({
              id: jobId,
              params: {},
            })
          )
            .unwrap()
            .then((res) => {
              setIsLoading(false);
            })
            .catch((error: any) => {
              notify(
                "error",
                `Failed to fetch job task list: ${error.response.data.message}`
              );
              setIsLoading(false);
            });
        })
        .catch((error: any) => {
          notify(
            "error",
            `Failed to create job task: ${error.response.data.message}`
          );
          setIsLoadingModalAddTask(false);
        });
    } else {
      setIsLoadingModalAddTask(true);
      dispatch(handleUpdateJobTask({ id: selectedJobTask.id, data: data }))
        .unwrap()
        .then((res) => {
          notify("success", "Job task updated successfully");
          setIsOpenModalAddTask(false);
          setSelectedJobTask(null);
          setIsLoadingModalAddTask(false);
          setIsLoading(true);
          dispatch(
            handleGetOverviewJobTaskList({
              id: jobId,
              params: {},
            })
          )
            .unwrap()
            .then((res) => {
              setIsLoading(false);
            })
            .catch((error: any) => {
              notify(
                "error",
                `Failed to fetch job task list: ${error.response.data.message}`
              );
              setIsLoading(false);
            });
        })
        .catch((error: any) => {
          notify(
            "error",
            `Failed to update job task: ${error.response.data.message}`
          );
          setIsLoadingModalAddTask(false);
        });
    }
  };

  return (
    <div className={cx("job-task-container")}>
      <div className={cx("header")}>
        <div className={cx("item")}>
          <div className={cx("search-container")}>
            <SearchIcon className={cx("search-icon")} />
            <input
              type="text"
              placeholder="Search"
              className={cx("search-input")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <PrimarySelectInput
            name="status"
            placeholder="All Tasks"
            value={statusFilter}
            onChange={(value: string) => setStatusFilter(value)}
            options={statusOptions}
            className={cx("status-select")}
            classNameIcon={cx("status-select-icon")}
            classNameList={cx("!min-w-[230px]")}
          />
        </div>

        <div className={cx("item")}>
          <PrimaryButton
            title="Add Task"
            icon={<PlusIcon className={cx("plus-icon")} />}
            onClick={handleOpenModalAddTask}
            className={cx("create-button")}
          />
        </div>
      </div>

      <div className={cx("body")}>
        {isLoading ? (
          <div className={cx("loading-overlay")}>
            <LoadingMainContent />
          </div>
        ) : (
          <BaseTable
            data={filteredJobTasks}
            columns={columns}
            page={1}
            totalPages={1}
            className={cx("table")}
          />
        )}
      </div>

      <AddNewTaskModal
        isOpen={isOpenModalAddTask}
        jobId={jobId}
        onClose={handleCloseModalAddTask}
        onSave={handleSaveModalAddTask}
        mode={modalMode}
        initialData={selectedJobTask}
        isLoading={isLoadingModalAddTask}
      />

      <ModalAlertDelete
        open={isOpendModalDelete}
        description={"Are you sure you want to delete this task?"}
        onCancel={() => {
          setIsOpendModalDelete(false);
        }}
        onConfirm={onDeleteWorkOrderConfirm}
      />
    </div>
  );
};

export default JobTaskTab;
